/*
 * astrak-lead-register.js
 * ---------------------------------------------------------------------------
 * THE REGISTER LEG.
 *
 * Every Astrak lead form is a native Shopify {% form 'contact' %}. That gives
 * us the email to Edwin, Shopify's spam protection and real success/error
 * states -- but Shopify stores nothing. This file is the storage half: at submit
 * time it copies the whole submission, including the message body and the full
 * route the visitor followed, into Klaviyo as a custom event.
 *
 * Why Klaviyo rather than a form app:
 *   - Astrak already pays for it (account RNuX9R, astrak.com).
 *   - /client/events is a documented public endpoint designed to be called from
 *     a browser with a PUBLIC key. Nothing secret ships in the theme.
 *   - Event properties are queryable, segmentable and exportable, so
 *     "how many leads, for what form, by channel" is a real report.
 *
 * Load AFTER astrak-lead-tracking.js -- that script stamps the route onto the
 * hidden inputs this one reads:
 *     <script src="{{ 'astrak-lead-tracking.js' | asset_url }}" defer></script>
 *     <script src="{{ 'astrak-lead-register.js' | asset_url }}" defer></script>
 *
 * FAILURE BEHAVIOUR IS DELIBERATE: this script never blocks, delays or cancels
 * a submit. If Klaviyo is down, the key is wrong, or the visitor blocks the
 * request, the form still posts and Edwin still gets the email. We lose a
 * register row, never a lead.
 *
 * DIAGNOSTIC TRADE-OFF: because failures are swallowed, a dropped register row
 * looks exactly like no attempt. Set window.ASTRAK_LEAD_DEBUG = true in the
 * console to log what is sent and what comes back while testing.
 *
 * PRIVACY: this creates a Klaviyo profile and an event. It does NOT subscribe
 * anyone to marketing -- that is a separate consent action in Klaviyo. Do not
 * add these profiles to a marketing list without a lawful basis.
 */
(function () {
  'use strict';

  /* Klaviyo PUBLIC key (site id). Safe to ship in a theme -- it is write-only
     and cannot read data back out. Astrak production account. */
  var COMPANY_ID = 'RNuX9R';
  var METRIC     = 'Astrak Lead Submitted';
  var REVISION   = '2024-10-15';
  var ENDPOINT   = 'https://a.klaviyo.com/client/events/?company_id=' + COMPANY_ID;

  function debug() {
    if (!window.ASTRAK_LEAD_DEBUG) return;
    try { console.log.apply(console, ['[astrak-lead]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* Hidden + visible inputs we lift onto the event, as name -> property.
     Anything absent on a given form is simply omitted. */
  var FIELDS = {
    /* the two reporting dimensions */
    form_id:          'form_id',
    form_name:        'form_name',
    part_type:        'part_type',
    /* routing */
    routing_summary:  'routing_summary',
    suggested_channel:'suggested_channel',
    /* geo + language */
    market:           'market',
    country:          'country',
    country_name:     'country_name',
    language:         'language',
    currency:         'currency',
    browser_language: 'browser_language',
    browser_timezone: 'browser_timezone',
    /* what they are asking about */
    machine:          'machine_typed',
    /* ALIAS, added 7 Sep 2026. snippets/astrak-range-enquiry.liquid names its
       machine input contact[Machine make and model], which was not in this map,
       so that value reached the notification email but never reached Klaviyo.
       Aliased rather than renamed on purpose: Shopify derives the notification
       email's field label from the input name, and renaming the input would
       degrade Edwin's triage email to a bare "Machine". Purely additive - both
       names land on the same machine_typed property, and val() returns '' for
       whichever one a given form does not have. */
    'Machine make and model': 'machine_typed',
    machine_make:     'machine_make',
    machine_model:    'machine_model',
    part_needed:      'part_needed',
    /* the page they filled it in on */
    form_page:        'form_page',
    product:          'product',
    product_url:      'product_url',
    sku:              'sku',
    compatible_skus:  'compatible_skus',
    collection:       'collection',
    /* the route they followed to get there */
    entry_page:       'entry_page',
    referrer:         'referrer',
    utm_source:       'utm_source',
    utm_medium:       'utm_medium',
    utm_campaign:     'utm_campaign',
    utm_term:         'utm_term',
    utm_content:      'utm_content',
    click_id:         'click_id',
    path_trail:       'path_trail',
    pages_viewed:     'pages_viewed',
    /* the enquiry itself */
    triage:           'triage_line',
    ack_subject:      'ack_subject',
    body:             'message',
    phone:            'phone',
    /* ADDED 7 Sep 2026. Postcode became a required field on every enquiry form
       on 1-2 Sep 2026 (contact[postcode]), but it was never in this map, so it
       reached the notification email and stopped there. Two spellings because
       sections/astrak-home-ca.liquid and the brochures page post
       contact[Postcode] with a capital P; both land on one property. */
    postcode:         'postcode',
    Postcode:         'postcode',
    /* ADDED 14 Sep 2026, when the US rubber track / rubber pad PPC snippets, the
       UK and US rubber-tracks range pages and the Canada homepage form were wired
       to this script. They name their inputs differently; alias rather than
       rename, so the notification email keeps its readable labels. */
    zip:                                       'postcode',
    'Source page':                             'source_page',
    'Machine make':                            'machine_make',
    'Machine make and model or part number':   'machine_typed',
    'Machine make and model or track size':    'machine_typed',
    'Machine model or track size':             'machine_model',
    'Machine model or pad part number':        'machine_model',
    'Track type needed':                       'part_needed',
    'Pad type needed':                         'part_needed',
    'Phone number':                            'phone',
    Phone:                                     'phone'
  };

  function val(form, name) {
    var el = form.querySelector('[name="contact[' + name + ']"]');
    if (!el) return '';
    return (el.value || '').trim();
  }

  /* Stops a double-click or a browser back-and-resubmit becoming two rows.
     Klaviyo dedupes on unique_id within a metric.

     THE PAGE IDENTITY MUST BE PART OF THIS KEY. The first version was
     form_id:email:minute, which silently discarded genuine enquiries: two
     submissions from the same person in the same minute about DIFFERENT parts
     produced an identical key, so Klaviyo dropped the second. Observed 19 Aug
     2026 - a French enquiry 27 seconds after an English one vanished with no
     error anywhere. Keep sku/product/page in the key. */
  function uniqueId(props, email) {
    var minute = Math.floor(Date.now() / 60000);
    var where = props.sku || props.product || props.form_page || '';
    return [props.form_id || 'form', email, where, minute].join(':');
  }

  function send(form) {
    var email = val(form, 'email');

    /* No email means Shopify will reject the submit anyway, and Klaviyo needs
       a profile identifier. Nothing useful to record. */
    if (!email) { debug('no email, skipping'); return; }

    /* Honeypot filled = bot. Keep it out of the register entirely.
       RENAMED 14 Sep 2026: the field used to be contact[company_website] with a
       "Company" label. Browser autofill recognised it and filled in real
       customers' company names, so genuine enquiries were dropped as bots
       (four in one weekend, 12-14 Sep 2026). contact[spam_check] with a
       "Leave this field empty" label matches no autofill heuristic. */
    if (val(form, 'spam_check')) { debug('honeypot filled, skipping'); return; }

    var props = {};
    Object.keys(FIELDS).forEach(function (name) {
      var v = val(form, name);
      if (v) props[FIELDS[name]] = v;
    });

    /* pages_viewed is the one field worth having as a number so it can be
       averaged and filtered with > and < in Klaviyo. */
    if (props.pages_viewed) {
      var n = parseInt(props.pages_viewed, 10);
      if (!isNaN(n)) props.pages_viewed = n;
    }

    /* How long the page had been open before submit, whole seconds. Humans take
       a while to type; bots post within a second or two. Recorded for filtering
       downstream, never used to drop a submission here. */
    try {
      if (window.performance && typeof performance.now === 'function') {
        props.seconds_on_page = Math.round(performance.now() / 1000);
      }
    } catch (e) {}

    props.submitted_at = new Date().toISOString();
    props.$event_id = uniqueId(props, email);

    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          unique_id: props.$event_id,
          metric: {
            data: { type: 'metric', attributes: { name: METRIC } }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                /* Phone deliberately stays in properties, not here: Klaviyo
                   rejects a profile whose phone_number is not valid E.164,
                   and that would lose the whole register row over a
                   formatting quibble. */
                /* Most forms post contact[name]. The contact page posts
                   contact[first_name] and contact[last_name] separately and has
                   no contact[name] at all, so without this fallback a named
                   customer is greeted "Hello there" by the acknowledgement
                   email. Check name first: it is what nine of ten forms use. */
                first_name: ((val(form, 'name') || val(form, 'Name')).split(' ')[0] || val(form, 'first_name') || val(form, 'First name') || val(form, 'First Name') || undefined),
                properties: {
                  last_lead_channel: props.suggested_channel || undefined,
                  last_lead_market: props.market || undefined
                }
              }
            }
          }
        }
      }
    };

    debug('sending', props.$event_id, props);

    /* keepalive lets the request outlive the page navigation the native form
       is about to trigger. sendBeacon cannot set the `revision` header Klaviyo
       requires, so fetch+keepalive is the right tool here. */
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'revision': REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (res) {
        debug('klaviyo responded', res.status);
      })['catch'](function (err) {
        debug('klaviyo request failed', err);
        /* never surface this to the customer */
      });
    } catch (e) { debug('fetch threw', e); /* ancient browser: lose the row, keep the lead */ }
  }

  function bind() {
    var forms = document.querySelectorAll('form[data-astrak-lead], form.qform, form.fh-form');
    debug('bound forms:', forms.length);
    Array.prototype.forEach.call(forms, function (form) {
      if (form.getAttribute('data-astrak-registered') === '1') return;
      form.setAttribute('data-astrak-registered', '1');
      form.addEventListener('submit', function () { send(form); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();